import { Hotspot } from 'hooks/queries/designs/useHotspots'
import { HotspotEdit } from './Types'
import { PromptSection } from './PromptSection'
import { ImageHotspotPromptView } from './ImageHotspotPromptView'
import { LogoHotspotPopoverView } from './LogoHotspotPopoverView'
import Styles from './ImageEditor.module.scss'
import { TextHotspotPopoverView } from './TextHotspotPopoverView'
import { Close, Trash2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import type { BrandKitLogoOption } from './LogoHotspotPopoverView'

interface MainViewProps {
  hotspot: Hotspot
  operations: HotspotEdit['type'][]
  /** Whether the current hotspot already has a text describe-change (update) edit queued. */
  hasTextDescribeEdit?: boolean
  hasImageAttach: boolean
  prompt: string
  imageUrls: string[]
  referenceImageUrl?: string | null
  onPromptChange: (value: string) => void
  onRemoveAttachedImage: (index: number) => void
  onPickImage: () => void
  /** Single reference replace for image hotspots; falls back to `onPickImage` if omitted. */
  onPickReferenceImage?: () => void
  onRemove: () => void
  onConfirmPrompt: () => void
  close: () => void
  brandKitLogos?: BrandKitLogoOption[]
  replaceLogoUrl?: string
  onSelectBrandKitLogoUrl?: (url: string) => void
  onPickLogoUpload?: () => void
  onConfirmLogo?: () => void
  onRemoveLogo?: () => void
}

const LABELS: Record<Hotspot['subject_type'], string> = {
  logo: 'Logo',
  call_to_action: 'Call to Action',
  image: 'Image',
  text_box: 'Text',
}

const isTextHotspot = (subjectType: Hotspot['subject_type']) =>
  subjectType === 'text_box' || subjectType === 'call_to_action'

export const MainView = ({
  hotspot,
  operations,
  hasTextDescribeEdit = false,
  hasImageAttach,
  prompt,
  imageUrls,
  referenceImageUrl = null,
  onPromptChange,
  onRemoveAttachedImage,
  onPickImage,
  onPickReferenceImage,
  onRemove,
  onConfirmPrompt,
  close,
  brandKitLogos = [],
  replaceLogoUrl = '',
  onSelectBrandKitLogoUrl,
  onPickLogoUpload,
  onConfirmLogo,
  onRemoveLogo,
}: MainViewProps) => {
  if (isTextHotspot(hotspot.subject_type)) {
    return (
      <TextHotspotPopoverView
        prompt={prompt}
        hasDescribeEdit={hasTextDescribeEdit}
        onPromptChange={onPromptChange}
        onDeleteText={onRemove}
        onConfirm={onConfirmPrompt}
        close={close}
      />
    )
  }

  if (hotspot.subject_type === 'logo') {
    return (
      <LogoHotspotPopoverView
        brandKitLogos={brandKitLogos}
        replaceLogoUrl={replaceLogoUrl}
        onSelectBrandKitLogoUrl={onSelectBrandKitLogoUrl ?? (() => {})}
        onPickLogoUpload={onPickLogoUpload ?? onPickImage}
        onConfirm={onConfirmLogo ?? onConfirmPrompt}
        onRemoveLogo={onRemoveLogo ?? onRemove}
        close={close}
      />
    )
  }

  if (hotspot.subject_type === 'image') {
    return (
      <ImageHotspotPromptView
        prompt={prompt}
        imageUrls={imageUrls}
        referenceImageUrl={referenceImageUrl}
        onPromptChange={onPromptChange}
        onRemoveAttachedImage={onRemoveAttachedImage}
        onPickImage={onPickReferenceImage ?? onPickImage}
        onConfirm={onConfirmPrompt}
        onCancel={close}
        close={close}
      />
    )
  }

  return (
    <>
      <div className={Styles.popoverHeader}>
        <div className={Styles.popoverHeaderTitle}>
          <Text variant="smallList">{LABELS[hotspot.subject_type]}</Text>
          <Text variant="metadata">{hotspot.description}</Text>
        </div>
        <IconButton variant="tertiary" size="sm" icon={Close} aria-label="Close" onPress={close} />
      </div>

      <Menu
        aria-label="Options"
        onAction={(key) => {
          if (key === 'remove') {
            onRemove()
          }
        }}
      >
        {[
          operations.includes('remove') && (
            <Item key="remove" textValue="Remove">
              <Trash2 />
              Remove
            </Item>
          ),
        ].filter(Boolean)}
      </Menu>

      <PromptSection
        prompt={prompt}
        imageUrls={imageUrls}
        showImageAttach={hasImageAttach}
        onPromptChange={onPromptChange}
        onRemoveAttachedImage={onRemoveAttachedImage}
        onPickImage={onPickImage}
        onConfirm={onConfirmPrompt}
      />
    </>
  )
}
